import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import LiveEditing from './Components/LiveEditing';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import { BusinessCentralMyPages } from './Components/BusinessCentralComponents/BusinessCentralMyPage';
import CountryPicker from './Components/CountryPicker';
import CountrySelector from './Components/CountrySelector';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('liveEditing')) {
        renderReact(
            <Provider store={store}>
                <LiveEditing />
            </Provider>,
            document.getElementById('liveEditing')
        );
    }
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('country-picker')) {
        renderReact(
            <Provider store={store}>
                <CountryPicker />
            </Provider>,
            document.getElementById('country-picker')
        );
    }

    if (document.getElementById('country-selector')) {
        renderReact(
            <Provider store={store}>
                <CountrySelector />
            </Provider>,
            document.getElementById('country-selector')
        );
    }

    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const useKustomCheckout = JSON.parse(document.getElementById('checkout').getAttribute('data-use-kustom-checkout'));
        const Checkout = DynamicComponent({
            loader: () => useKustomCheckout ? import('./Components/Checkout') : import('./Components/Checkout/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Components/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(
            ({ lightboxImages }) => {
                store.injectReducer('lightboxImages', lightboxImages);
                const rootElement = document.getElementById('lightBoxImages');
                const images = Array.from(
                    rootElement.querySelectorAll('template')
                ).map((img) => ({
                    html: img.innerHTML,
                    src: img.dataset.src,
                    type: img.dataset.type || 'image',
                }));
                renderReact(
                    <Provider store={store}>
                        <LightboxImages images={images} />
                    </Provider>,
                    document.getElementById('lightBoxImages')
                );
            }
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [...slider.querySelectorAll('template')].map(
                    (block) => {
                        return {
                            html: block.innerHTML,
                        };
                    }
                );
                if (values.length > 0) {
                    renderReact(<Slider values={values} />, slider);
                }
            }
        );
    }

    if (document.querySelectorAll('.marquee').length > 0) {
        const Marquee = DynamicComponent({
            loader: () => import('./Components/Marquee'),
        });
        Array.from(document.querySelectorAll('.marquee')).forEach(
            (marquee, index) => {
                const objects = [
                    ...marquee.querySelectorAll('.marquee__object'),
                ].map((marqueeObject) => {
                    return {
                        html: marqueeObject.innerHTML,
                    };
                });
                console.log(objects);
                if (objects.length > 0) {
                    renderReact(<Marquee objects={objects} />, marquee);
                }
            }
        );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }

    if (document.getElementById('lang-selector')) {
        const languages = store.getState().navigation.languages;

        if (languages.length > 1) {
            const LanguageSelector = DynamicComponent({
                loader: () => import('./Components/LanguageSelector'),
            });
            renderReact(
                <Provider store={store}>
                    <LanguageSelector />
                </Provider>,
                document.getElementById('lang-selector')
            );
        } else {
            document.getElementById('lang-selector').remove();
        }
    }

    if (document.getElementById('lang-selector-mobile')) {
        const element = document.getElementById('lang-selector-mobile');
        const languages = JSON.parse(element.getAttribute('data-languages'));

        const LanguageSelector = DynamicComponent({
            loader: () => import('./Components/LanguageSelector'),
        });
        renderReact(
            <Provider store={store}>
                <LanguageSelector languages={languages} />
            </Provider>,
            document.getElementById('lang-selector-mobile')
        );
    }

    if (document.getElementById('footer-data')) {
        const element = document.getElementById('footer-data');
        const links = JSON.parse(element.getAttribute('data-model'));

        const FooterLinks = DynamicComponent({
            loader: () => import('./Components/FooterLinks'),
        });

        renderReact(
            <Provider store={store}>
                <FooterLinks links={links} />
            </Provider>,
            document.getElementById('footer-data')
        );
    }

    if (document.getElementById('product-detail-accordion')) {
        const element = document.getElementById('product-detail-accordion');
        const model = JSON.parse(element.getAttribute('data-model'));

        const ProductAccordion = DynamicComponent({
            loader: () => import('./Components/ProductAccordion'),
        });

        renderReact(
            <Provider store={store}>
                <ProductAccordion {...model} />
            </Provider>,
            document.getElementById('product-detail-accordion')
        );
    }

    if (document.getElementById('apply-for-account-form')) {
        const element = document.getElementById('apply-for-account-form');
        const languages = JSON.parse(element.getAttribute('data-culturefino'));
        const orgs = JSON.parse(element.getAttribute('data-listorgs'));

        const SelectCountryAndCompanyLogin = DynamicComponent({
            loader: () => import('./Components/SelectCountryAndCompanyLogin'),
        });
        renderReact(
            <Provider store={store}>
                <SelectCountryAndCompanyLogin
                    countries={languages}
                    orgs={orgs}
                />
            </Provider>,
            document.getElementById('apply-for-account-form')
        );
    }

    if (document.getElementById('BusinessCentral')) {
        const isNonBCWebsite = JSON.parse(document.getElementById('BusinessCentral').getAttribute('data-is-non-bc-website'));
        renderReact(
            <Provider store={store}>
                <BusinessCentralMyPages isNonBCWebsite={isNonBCWebsite} />
            </Provider>,
            document.getElementById('BusinessCentral')
        );
    }

    if (document.querySelectorAll('wishlist-button').length > 0) {
        const WishlistButton = DynamicComponent({
            loader: () => import('./Components/WishListButton.container'),
        });
        Array.from(document.querySelectorAll('wishlist-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <WishlistButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.getElementById('wishlist')) {
        const WishList = DynamicComponent({
            loader: () => import('./Components/WishList.container'),
        });
        renderReact(
            <Provider store={store}>
                <WishList />
            </Provider>,
            document.getElementById('wishlist')
        );
    }
    if (document.getElementById('wishlist-button-container')) {
        const WishListAdd = DynamicComponent({
            loader: () => import('./Components/WishList.add'),
        });
        renderReact(
            <Provider store={store}>
                <WishListAdd />
            </Provider>,
            document.getElementById('wishlist-button-add')
        );
    }

    if (document.getElementById('onboarding')) {
        const element = document.getElementById('onboarding');
        const heading = element.getAttribute('data-heading"');
        const content = element.getAttribute('data-content');

        const Onboarding = DynamicComponent({
            loader: () => import('./Components/Onboarding'),
        });
        renderReact(
            <Provider store={store}>
                <Onboarding heading={heading} content={content} />
            </Provider>,
            document.getElementById('onboarding')
        );
    }

    if (document.getElementById('variant-list')) {
        const element = document.getElementById('variant-list');
        const model = JSON.parse(element.getAttribute('data-model'));

        const VariantData = DynamicComponent({
            loader: () => import('./Components/Products/VariantData'),
        });

        renderReact(
            <Provider store={store}>
                <VariantData {...model} />
            </Provider>,
            document.getElementById('variant-list')
        );
    }

    if (document.getElementById('product-info-accordion')) {
        var acc = document.getElementsByClassName('product-info__accordion');
        var i;

        for (i = 0; i < acc.length; i++) {
            acc[i].addEventListener('click', function () {
                this.classList.toggle('product-info__accordion--active');

                var panel = this.nextElementSibling;
                if (panel.style.display === 'block') {
                    panel.style.display = 'none';
                } else {
                    panel.style.display = 'block';
                }
            });
        }
    }

    if (document.querySelectorAll('.product__buy-button-item').length > 0) {
        const ProductQuantity = DynamicComponent({
            loader: () => import('./Components/Products/ProductQuantity'),
        });
        Array.from(
            document.querySelectorAll('.product__buy-button-item')
        ).forEach((button) => {
            const {
                articlenumber,
                quantityfieldid,
                minquantity,
                stockquantity,
                isorderitem,
                comment,
            } = button.dataset;

            renderReact(
                <Provider store={store}>
                    <ProductQuantity
                        articleNumber={articlenumber}
                        quantityFieldId={quantityfieldid}
                        minQuantity={+minquantity}
                        stockQuantity={+stockquantity}
                        isOrderItem={isorderitem}
                        comment={comment}
                    />
                </Provider>,
                button
            );
        });
    }

    if (document.getElementById('faq')) {
        const element = document.getElementById('faq');
        const model = JSON.parse(element.getAttribute('data-model'));

        const Faq = DynamicComponent({
            loader: () => import('./Components/Faq'),
        });

        renderReact(
            <Provider store={store}>
                <Faq item={model} />
            </Provider>,
            document.getElementById('faq')
        );
    }

    if (document.getElementById('bannerhero-video-button')) {
        const videos = document.querySelectorAll('video#bannerhero-video');
        const buttons = document.querySelectorAll(
            'button#bannerhero-video-button'
        );
        const overlayButtons = document.querySelectorAll(
            'button#bannerhero-video__overlay-button'
        );

        buttons.forEach((button, i) => {
            const video = videos[i];
            const overlaybutton = overlayButtons[i];
            button.addEventListener('click', function () {
                if (video.paused == true) {
                    video.play();
                    button.classList.add(
                        'bannerhero__content__video-button--hidden'
                    );
                    overlaybutton.classList.remove(
                        'bannerhero__content__video-overlay-button--hidden'
                    );
                    overlaybutton.classList.add(
                        'bannerhero__content__video-overlay-button'
                    );
                } else {
                    video.pause();
                    overlaybutton.classList.add(
                        'bannerhero__content__video-overlay-button--hidden'
                    );
                }
            });
        });

        overlayButtons.forEach((overlaybutton, i) => {
            const video = videos[i];
            const button = buttons[i];
            overlaybutton.addEventListener('click', function () {
                if (video.paused == false) {
                    video.pause();
                    overlaybutton.classList.add(
                        'bannerhero__content__video-overlay-button--hidden'
                    );
                    button.classList.remove(
                        'bannerhero__content__video-button--hidden'
                    );
                }
            });
        });
    }
};

bootstrapComponents();
